import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "../components/ui/sonner";

const API = process.env.REACT_APP_BACKEND_URL || "";

export default function KitPage() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("config");
  const [openTooltip, setOpenTooltip] = useState(null);
  const [form, setForm] = useState({
    nombre_bot: "Kit",
    nombre_concesionaria: "",
    inventario: "",
    whatsapp_numero: "",
    vendedor_email: "",
    gmail_user: "",
    gmail_pass: "",
    google_calendar_id: "",
    google_client_id: "",
    google_client_secret: "",
    google_refresh_token: "",
    openai_api_key: "",
    activo: true,
    plan: "trial",
    fecha_inicio_trial: "",
    fecha_fin_trial: ""
  });

  useEffect(() => { fetchData(); }, []);

  async function fetchData() {
    setLoading(true);
    try {
      const [configRes, leadsRes, statsRes] = await Promise.all([
        fetch(`${API}/api/kit/config`, { credentials: "include" }),
        fetch(`${API}/api/kit/leads`, { credentials: "include" }),
        fetch(`${API}/api/kit/leads/stats`, { credentials: "include" })
      ]);
      const configData = await configRes.json();
      const leadsData = await leadsRes.json();
      const statsData = await statsRes.json();
      if (configData.config) {
        setConfig(configData.config);
        setForm(f => ({ ...f, ...configData.config }));
      }
      setLeads(leadsData.leads || []);
      setStats(statsData.stats || null);
    } catch (e) {
      toast.error("Error cargando datos");
    }
    setLoading(false);
  }

  async function saveConfig() {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/kit/config`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Configuración guardada");
        setConfig(data.config);
      } else {
        toast.error("Error al guardar");
      }
    } catch (e) {
      toast.error("Error al guardar");
    }
    setSaving(false);
  }

  async function toggleKit() {
    try {
      const res = await fetch(`${API}/api/kit/config/toggle`, {
        method: "PATCH",
        credentials: "include"
      });
      const data = await res.json();
      if (data.success) {
        toast.success(data.activo ? "Kit activado" : "Kit desactivado");
        fetchData();
      }
    } catch (e) {
      toast.error("Error");
    }
  }

  const clasificacionColor = (c) => {
    if (!c) return "bg-gray-100 text-gray-600";
    if (c.includes("CALIENTE")) return "bg-red-100 text-red-700";
    if (c.includes("TIBIO")) return "bg-yellow-100 text-yellow-700";
    return "bg-blue-100 text-blue-700";
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <button
        onClick={() => navigate('/dashboard')}
        data-testid="kit-back-to-dashboard"
        className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-4 transition-colors"
      >
        ← Volver al Dashboard
      </button>
      {/* WhatsApp info card (connection happens from the Dashboard) */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 mb-6" data-testid="kit-whatsapp-info">
        <div className="flex items-start gap-3">
          <div className="text-2xl">📱</div>
          <div className="flex-1">
            <h3 className="text-base font-semibold text-blue-900 mb-1">Conexión a WhatsApp</h3>
            <p className="text-sm text-blue-800 mb-3">
              Para activar tu bot Kit AutoBot en WhatsApp, ve al Dashboard y haz clic en
              <strong> "Activar Asistente"</strong> en la tarjeta de tu bot Kit. Allí podrás escanear el código QR.
            </p>
            <button
              onClick={() => navigate('/dashboard')}
              data-testid="kit-go-to-dashboard-btn"
              className="inline-flex items-center gap-2 bg-blue-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors text-sm"
            >
              Ir al Dashboard →
            </button>
          </div>
        </div>
      </div>
      {/* Tutorial video */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 mb-6 shadow-sm" data-testid="kit-tutorial-video">
        <h3 className="text-base font-semibold text-gray-900 mb-3">¿Cómo configurar tu Kit AutoBot?</h3>
        <div style={{ position: 'relative', paddingTop: '56.25%' }}>
          <iframe
            src="https://player.mediadelivery.net/embed/672206/94fd29eb-3a1e-420f-9e67-8d678eb6bd1a?autoplay=false&loop=false&muted=false&preload=true&responsive=true"
            loading="lazy"
            style={{ border: 0, position: 'absolute', top: 0, height: '100%', width: '100%' }}
            allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture;fullscreen;"
            allowFullScreen
            title="Cómo configurar tu Kit AutoBot"
          />
        </div>
      </div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">🚗 Kit AutoBot</h1>
          <p className="text-gray-500 text-sm mt-1">Asistente virtual para concesionarias</p>
        </div>
        {config && (
          <button
            onClick={toggleKit}
            data-testid="kit-toggle-btn"
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              config.activo
                ? "bg-green-100 text-green-700 hover:bg-green-200"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            {config.activo ? "✅ Activo" : "⏸️ Inactivo"}
          </button>
        )}
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          {[
            { label: "Total leads", value: stats.total, color: "indigo" },
            { label: "🔥 Calientes", value: stats.calientes, color: "red" },
            { label: "🌡️ Tibios", value: stats.tibios, color: "yellow" },
            { label: "❄️ Fríos", value: stats.frios, color: "blue" },
            { label: "📅 Con cita", value: stats.con_cita, color: "green" }
          ].map((s, i) => (
            <div key={i} className="bg-white rounded-xl border p-4 text-center">
              <div className="text-2xl font-bold text-gray-900">{s.value}</div>
              <div className="text-xs text-gray-500 mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b">
        {["config", "leads"].map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            data-testid={`kit-tab-${t}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === t
                ? "border-indigo-600 text-indigo-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            {t === "config" ? "⚙️ Configuración" : "👥 Leads"}
          </button>
        ))}
      </div>

      {/* Config Tab */}
      {tab === "config" && (
        <div className="bg-white rounded-xl border p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { key: "nombre_bot", label: "Nombre del bot", placeholder: "Kit" },
              { key: "nombre_concesionaria", label: "Nombre concesionaria", placeholder: "AutoDemo Concesionaria" },
              {
                key: "inventario",
                label: "Inventario de autos",
                type: "textarea",
                placeholder: `Ejemplos:
Toyota Corolla 2020 - Automático - 45.000 km - $12.000.000 - Excelente estado
→ https://link-publicacion.cl

Ver todo nuestro inventario: https://mi-pagina.cl`,
                helpText: "Agrega aquí la información de tus autos disponibles. Incluye marca, modelo, año, kilometraje, precio, estado y links de publicaciones. El bot usará esta información para responder preguntas de los clientes.",
                fullWidth: true
              },
              { key: "vendedor_email", label: "Email del vendedor", placeholder: "ventas@concesionaria.cl" },
              {
                key: "gmail_user",
                label: "Gmail de la concesionaria",
                placeholder: "concesionaria@gmail.com"
              },
              {
                key: "gmail_pass",
                label: "Contraseña de app Gmail",
                placeholder: "xxxx xxxx xxxx xxxx",
                type: "password",
                help: {
                  title: "¿Cómo configurarlo?",
                  steps: [
                    { text: 'Activa la Verificación en 2 pasos: ', link: { href: 'https://myaccount.google.com/signinoptions/two-step-verification', label: 'myaccount.google.com/signinoptions/two-step-verification' } },
                    { text: 'Ve a Contraseñas de aplicaciones: ', link: { href: 'https://myaccount.google.com/apppasswords', label: 'myaccount.google.com/apppasswords' } },
                    { text: 'En "Nombre de la app" escribe "Kit AutoBot"' },
                    { text: 'Haz clic en "Crear"' },
                    { text: 'Copia la contraseña de 16 caracteres y pégala en el campo "Contraseña de app Gmail"' },
                  ]
                }
              },
              {
                key: "google_calendar_id",
                label: "Google Calendar ID",
                placeholder: "xxxxx@group.calendar.google.com",
                help: {
                  title: "¿Cómo obtenerlo? (importante: 2 pasos)",
                  steps: [
                    { text: 'Ve a ', link: { href: 'https://calendar.google.com', label: 'calendar.google.com' } },
                    { text: 'En la barra lateral izquierda busca "Mis calendarios"' },
                    { text: 'Pasa el mouse por encima del calendario que quieres usar' },
                    { text: 'Haz clic en los 3 puntos (⋮) que aparecen y selecciona "Config. y uso compartido"' },
                    { text: 'En la sección "Se compartió con" click en "+ Agregar personas y grupos" → escribe kit-autobot-calendar@consesionarias.iam.gserviceaccount.com → permiso: "Hacer cambios en eventos" → Enviar' },
                    { text: 'En la misma página, baja hasta "Integrar calendario"' },
                    { text: 'Copia el "ID del calendario" (puede ser tu email o un código largo) y pégalo aquí' },
                  ],
                  warning: '⚠️ El paso 5 (compartir con kit-autobot-calendar@consesionarias.iam.gserviceaccount.com) es obligatorio. Sin esto el bot no podrá agendar en tu calendario.'
                }
              },
              {
                key: "openai_api_key",
                label: "OpenAI API Key",
                placeholder: "sk-proj-...",
                type: "password",
                help: {
                  title: "¿Cómo obtenerla?",
                  steps: [
                    { text: 'Ve a ', link: { href: 'https://platform.openai.com/api-keys', label: 'platform.openai.com/api-keys' } },
                    { text: 'Inicia sesión o crea una cuenta' },
                    { text: 'Haz clic en "Create new secret key"' },
                    { text: 'Copia la key (empieza con sk-proj-)' },
                  ],
                  warning: '⚠️ Guárdala en un lugar seguro — no podrás verla nuevamente'
                }
              },
            ].map(({ key, label, placeholder, type, help, helpText, fullWidth }) => (
              <div key={key} className={fullWidth ? "md:col-span-2" : undefined}>
                <div className="flex items-center gap-1.5 mb-1">
                  <label className="block text-sm font-medium text-gray-700">{label}</label>
                  {help && (
                    <div className="relative inline-block group">
                      <button
                        type="button"
                        onClick={() => setOpenTooltip(openTooltip === key ? null : key)}
                        onBlur={() => setTimeout(() => setOpenTooltip(t => t === key ? null : t), 150)}
                        aria-label={`Ayuda para ${label}`}
                        data-testid={`kit-help-${key}`}
                        className="w-4 h-4 flex items-center justify-center rounded-full bg-gray-200 text-gray-600 text-[10px] font-bold hover:bg-indigo-500 hover:text-white transition-colors cursor-help"
                      >
                        ?
                      </button>
                      <div
                        data-testid={`kit-tooltip-${key}`}
                        className={`absolute z-30 left-0 top-6 w-72 sm:w-80 bg-white rounded-lg shadow-xl border border-gray-200 p-3 text-xs text-gray-700 transition-opacity duration-150 ${
                          openTooltip === key
                            ? 'opacity-100 visible'
                            : 'opacity-0 invisible group-hover:opacity-100 group-hover:visible'
                        }`}
                      >
                        <p className="font-semibold text-gray-900 mb-2">{help.title}</p>
                        <ol className="list-decimal list-inside space-y-1.5">
                          {help.steps.map((s, i) => (
                            <li key={i} className="leading-snug">
                              {s.text}
                              {s.link && (
                                <a
                                  href={s.link.href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-indigo-600 hover:underline break-all"
                                >
                                  {s.link.label}
                                </a>
                              )}
                            </li>
                          ))}
                        </ol>
                        {help.warning && (
                          <p className="mt-2 pt-2 border-t border-gray-100 text-amber-700">{help.warning}</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {type === "textarea" ? (
                  <textarea
                    rows={6}
                    value={form[key] || ""}
                    onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                    placeholder={placeholder}
                    data-testid={`kit-input-${key}`}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-y font-mono"
                  />
                ) : (
                  <input
                    type={type || "text"}
                    value={form[key] || ""}
                    onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                    placeholder={placeholder}
                    data-testid={`kit-input-${key}`}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                )}
                {helpText && (
                  <p className="mt-1.5 text-xs text-gray-500 leading-relaxed">{helpText}</p>
                )}
              </div>
            ))}
          </div>

          <button
            onClick={saveConfig}
            disabled={saving}
            data-testid="kit-save-btn"
            className="w-full bg-indigo-600 text-white py-2 rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors"
          >
            {saving ? "Guardando..." : "Guardar configuración"}
          </button>
        </div>
      )}

      {/* Leads Tab */}
      {tab === "leads" && (
        <div className="bg-white rounded-xl border overflow-hidden">
          {leads.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <div className="text-4xl mb-2">🚗</div>
              <p>Aún no hay leads registrados</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    {["Nombre", "Teléfono", "Auto", "Presupuesto", "Cita", "Score", "Estado"].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {leads.map((lead, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">{lead.nombre || "-"}</td>
                      <td className="px-4 py-3 text-gray-500">{lead.telefono || "-"}</td>
                      <td className="px-4 py-3">{lead.tipo_auto || "-"}</td>
                      <td className="px-4 py-3">{lead.presupuesto || "-"}</td>
                      <td className="px-4 py-3 text-gray-500">
                        {lead.fecha_visita ? `${lead.fecha_visita} ${lead.hora_visita || ""}` : "-"}
                      </td>
                      <td className="px-4 py-3 font-bold">{lead.score || "-"}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${clasificacionColor(lead.clasificacion)}`}>
                          {lead.clasificacion || "-"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
